import React from "react"
import { AppState, AppStateStatus } from "react-native"
import { getPathFromState } from "@react-navigation/native"
import { log } from "../index"
import impressionAggregator from "../impression-aggregator"
import Analytics from "../analytics"

type TrackingContextValue = {
  onNavigationReady: (navigationRef: React.MutableRefObject<any>, linking: any) => void
  onNavigationStateChange: (
    state: any, navigationRef: React.MutableRefObject<any>, linking: any
  ) => void
}

const TrackingContext = React.createContext<TrackingContextValue | undefined>(undefined)

// Centralized mapping of route names to Mixpanel screen identifiers
const screenMap: Record<string, string> = {
  // onboarding
  "Welcome": "onboarding-welcome",
  "Preferences": "onboarding-preferences",
  "BasicInfo": "onboarding-basic-info",
  "ValueSelection": "onboarding-value-selection",
  "AddAccounts": "onboarding-add-accounts",
  "Complete": "onboarding-complete",
  // in-app
  "DashboardHome": "dashboard",
  "TransactionsHome": "transactions",
  "ExploreCompaniesHome": "explore-companies",
  "CompanySearchResults": "company-search-results",
  "Company": "company",
  "SettingsHome": "settings",
  "ManageDemographics": "manage-demographics",
  "ManageAccounts": "manage-accounts",
  "ManageMfa": "manage-mfa",
  "InstitutionDetails": "institution-details",
  "DeleteAccountHome": "delete-account",
}

export const TrackingProvider = ({ children }: { children: React.ReactNode }) => {
  const routeNameRef = React.useRef<string | undefined>(undefined)
  const routeKeyRef = React.useRef<string | undefined>(undefined)
  const routePathRef = React.useRef<string | undefined>(undefined)
  const routeQueryStringRef = React.useRef<string | undefined>(undefined)
  const routeParamsObjectRef = React.useRef<any | undefined>(undefined)
  const screenStartTimeRef = React.useRef<number | undefined>(undefined)
  const appStateRef = React.useRef<AppStateStatus | undefined>(undefined)

  const sendPageView = React.useCallback((previousRouteName?: string) => {
    if (!previousRouteName || !screenStartTimeRef.current) return

    const now = Date.now()
    const durationMs = now - screenStartTimeRef.current

    try {
      const queryParams = routeQueryStringRef.current
        ? Object.fromEntries(new URLSearchParams(routeQueryStringRef.current))
        : undefined

      const screen = screenMap[previousRouteName]

      if (!screen) {
        log.warn(`[ScreenTracker] No tracking name found for screen: ${previousRouteName}`)
        return
      }

      const seenLogos = impressionAggregator.company.getAndClear()
      const companyImpressions = seenLogos.length > 0 ? seenLogos : undefined

      const seenPromotions = impressionAggregator.promotion.getAndClear()
      const promotionImpressions = seenPromotions.length > 0 ? seenPromotions : undefined

      const eventProperties = {
        screen,
        query: queryParams,
        durationMs,
        durationSeconds: Math.round(durationMs / 1000),
        companyImpressions,
        promotionImpressions,
      }
      // console.log('page_view', JSON.stringify(eventProperties, null, 2))
      Analytics.track('page_view', eventProperties)
    } catch (e) {
      log.error(e, { tags: { message: 'analytics track error' } })
    }
  }, [])

  const onNavigationReady = React.useCallback((
    navigationRef: React.MutableRefObject<any>, linking: any
  ) => {
    // initialize current route and timer for first screen
    const currentRoute = navigationRef.current?.getCurrentRoute?.()
    routeNameRef.current = currentRoute?.name
    routeKeyRef.current = currentRoute?.key
    screenStartTimeRef.current = Date.now()

    try {
      const initialState = (
        (navigationRef.current as any)?.getRootState?.() || navigationRef.current?.getState?.()
      )
      if (initialState) {
        const initialPath = getPathFromState(initialState, linking.config)
        routePathRef.current = initialPath
        const split = initialPath.split('?')
        routeQueryStringRef.current = split.length > 1 ? split[1] : undefined
      }
      routeParamsObjectRef.current = currentRoute?.params
    } catch (e) {
      log.error(e, { tags: { message: 'error capturing initial path + params' } })
    }
  }, [])

  const onNavigationStateChange = React.useCallback((
    state: any, navigationRef: React.MutableRefObject<any>, linking: any
  ) => {
    const previousRouteName = routeNameRef.current
    const currentRoute = navigationRef.current?.getCurrentRoute?.()
    const currentRouteName = currentRoute?.name
    const currentRouteKey = currentRoute?.key
    const currentFullPath = state ? getPathFromState(state, linking.config) : undefined
    const split = currentFullPath ? currentFullPath.split('?') : []
    const currentQueryString = split.length > 1 ? split[1] : undefined

    const routeParamsString = JSON.stringify(routeParamsObjectRef.current || null)
    const currentParamsString = JSON.stringify(currentRoute?.params || null)
    const paramsChanged = routeParamsString !== currentParamsString
    const keyChanged = routeKeyRef.current !== currentRouteKey
    const pathChanged = !!currentFullPath && routePathRef.current !== currentFullPath

    if (pathChanged || paramsChanged || keyChanged) {
      // record how long previous screen was viewed, then reset timer
      sendPageView(previousRouteName)
      screenStartTimeRef.current = Date.now()
    }

    routeNameRef.current = currentRouteName
    routeKeyRef.current = currentRouteKey
    routePathRef.current = currentFullPath
    routeQueryStringRef.current = currentQueryString
    routeParamsObjectRef.current = currentRoute?.params
  }, [sendPageView])

  React.useEffect(() => {
    const handleAppStateChange = (nextState: AppStateStatus) => {
      const prevState = appStateRef.current
      appStateRef.current = nextState

      // When the app transitions away from active, end the timer and send a report
      if ((prevState === 'active' || prevState === undefined) && (nextState === 'inactive' || nextState === 'background')) {
        sendPageView(routeNameRef.current)
      }

      // When the app becomes active, start/restart the timer for the current screen
      if (nextState === 'active') {
        screenStartTimeRef.current = Date.now()
      }
    }

    const subscription = AppState.addEventListener('change', handleAppStateChange)
    return () => subscription.remove()
  }, [sendPageView])

  const value = React.useMemo<TrackingContextValue>(() => ({
    onNavigationReady,
    onNavigationStateChange,
  }), [onNavigationReady, onNavigationStateChange])

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  )
}

export const useTracking = (): TrackingContextValue => {
  const ctx = React.useContext(TrackingContext)
  if (!ctx) throw new Error('useTracking must be used within a TrackingProvider')
  return ctx
}


